'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

export interface AriaButtonProps {
  'aria-label'?: string
  'aria-describedby'?: string
  'aria-pressed'?: boolean
  'aria-expanded'?: boolean
  'aria-haspopup'?: boolean | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog'
  'aria-controls'?: string
  'aria-current'?: boolean | 'page' | 'step' | 'location' | 'date' | 'time'
  role?: string
  tabIndex?: number
}

export interface KeyboardNavigationProps {
  onEnter?: () => void
  onSpace?: () => void
  onEscape?: () => void
  onArrowUp?: () => void
  onArrowDown?: () => void
  onArrowLeft?: () => void
  onArrowRight?: () => void
  onHome?: () => void
  onEnd?: () => void
  preventDefault?: boolean
}

export interface FocusManagementProps {
  autoFocus?: boolean
  restoreFocus?: boolean
  trapFocus?: boolean
  initialFocusRef?: React.RefObject<HTMLElement>
}

/**
 * Hook for managing ARIA attributes on interactive elements
 */
export function useAriaButton(
  isPressed?: boolean,
  isExpanded?: boolean,
  label?: string,
  controls?: string,
  current?: boolean | 'page' | 'step' | 'location' | 'date' | 'time'
): AriaButtonProps {
  return {
    ...(label && { 'aria-label': label }),
    ...(typeof isPressed === 'boolean' && { 'aria-pressed': isPressed }),
    ...(typeof isExpanded === 'boolean' && { 'aria-expanded': isExpanded }),
    ...(controls && { 'aria-controls': controls }),
    ...(typeof current === 'boolean' && { 'aria-current': current }),
    ...(typeof current === 'string' && { 'aria-current': current }),
  }
}

/**
 * Hook for keyboard navigation support
 */
export function useKeyboardNavigation({
  onEnter,
  onSpace,
  onEscape,
  onArrowUp,
  onArrowDown,
  onArrowLeft,
  onArrowRight,
  onHome,
  onEnd,
  preventDefault = true
}: KeyboardNavigationProps) {
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Enter':
        if (onEnter) {
          if (preventDefault) event.preventDefault()
          onEnter()
        }
        break
      case ' ':
        if (onSpace) {
          if (preventDefault) event.preventDefault()
          onSpace()
        }
        break
      case 'Escape':
        if (onEscape) {
          if (preventDefault) event.preventDefault()
          onEscape()
        }
        break
      case 'ArrowUp':
        if (onArrowUp) {
          if (preventDefault) event.preventDefault()
          onArrowUp()
        }
        break
      case 'ArrowDown':
        if (onArrowDown) {
          if (preventDefault) event.preventDefault()
          onArrowDown()
        }
        break
      case 'ArrowLeft':
        if (onArrowLeft) {
          if (preventDefault) event.preventDefault()
          onArrowLeft()
        }
        break
      case 'ArrowRight':
        if (onArrowRight) {
          if (preventDefault) event.preventDefault()
          onArrowRight()
        }
        break
      case 'Home':
        if (onHome) {
          if (preventDefault) event.preventDefault()
          onHome()
        }
        break
      case 'End':
        if (onEnd) {
          if (preventDefault) event.preventDefault()
          onEnd()
        }
        break
    }
  }, [onEnter, onSpace, onEscape, onArrowUp, onArrowDown, onArrowLeft, onArrowRight, onHome, onEnd, preventDefault])

  return { onKeyDown: handleKeyDown }
}

/**
 * Hook for focus management in modals and panels
 */
export function useFocusManagement({
  autoFocus = false,
  restoreFocus = true,
  trapFocus = false,
  initialFocusRef
}: FocusManagementProps = {}) {
  const containerRef = useRef<HTMLElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  // Store the previously focused element
  useEffect(() => {
    if (restoreFocus) {
      previousFocusRef.current = document.activeElement as HTMLElement
    }
  }, [restoreFocus])

  // Auto-focus the initial element or first focusable element
  useEffect(() => {
    if (!autoFocus) return

    const focusElement = () => {
      if (initialFocusRef?.current) {
        initialFocusRef.current.focus()
      } else if (containerRef.current) {
        const focusableElements = containerRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
        const firstFocusable = focusableElements[0] as HTMLElement
        if (firstFocusable) {
          firstFocusable.focus()
        }
      }
    }

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(focusElement, 100)
    return () => clearTimeout(timeoutId)
  }, [autoFocus, initialFocusRef])

  // Focus trap implementation
  useEffect(() => {
    if (!trapFocus || !containerRef.current) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return

      const focusableElements = containerRef.current!.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      const firstElement = focusableElements[0] as HTMLElement
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault()
          lastElement.focus()
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault()
          firstElement.focus()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [trapFocus])

  // Restore focus when component unmounts
  useEffect(() => {
    return () => {
      if (restoreFocus && previousFocusRef.current) {
        previousFocusRef.current.focus()
      }
    }
  }, [restoreFocus])

  return { containerRef }
}

/**
 * Hook for managing ARIA live regions for dynamic content updates
 */
export function useLiveRegion(politeness: 'polite' | 'assertive' = 'polite') {
  const [message, setMessage] = useState<string>('')
  const liveRegionRef = useRef<HTMLDivElement>(null)

  const announce = useCallback((text: string) => {
    setMessage(text)
    // Clear the message after a brief delay to allow re-announcement of the same text
    setTimeout(() => setMessage(''), 1000)
  }, [])

  return {
    announce,
    liveRegionProps: {
      ref: liveRegionRef,
      'aria-live': politeness,
      'aria-atomic': true,
      style: { position: 'absolute' as const, left: '-10000px', width: '1px', height: '1px', overflow: 'hidden' as const }
    },
    message
  }
}

/**
 * Hook for managing skip links
 */
export function useSkipLinks() {
  const skipToContent = useCallback(() => {
    const mainContent = document.querySelector('main') || document.querySelector('[role="main"]')
    if (mainContent && mainContent instanceof HTMLElement) {
      mainContent.focus()
      mainContent.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  const skipToNavigation = useCallback(() => {
    const navigation = document.querySelector('nav') || document.querySelector('[role="navigation"]')
    if (navigation && navigation instanceof HTMLElement) {
      navigation.focus()
      navigation.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  return { skipToContent, skipToNavigation }
}

/**
 * Hook for managing disclosure/collapsible elements
 */
export function useDisclosure(isOpen: boolean = false) {
  const [isExpanded, setIsExpanded] = useState(isOpen)
  const contentRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const toggle = useCallback(() => {
    setIsExpanded(prev => !prev)
  }, [])

  const open = useCallback(() => {
    setIsExpanded(true)
  }, [])

  const close = useCallback(() => {
    setIsExpanded(false)
  }, [])

  // Handle keyboard navigation for disclosure
  const keyboardProps = useKeyboardNavigation({
    onEnter: toggle,
    onSpace: toggle,
    onEscape: close
  })

  const triggerProps = {
    ...useAriaButton(isExpanded, isExpanded, undefined, contentRef.current?.id),
    ...keyboardProps,
    ref: triggerRef,
    onClick: toggle
  }

  const contentProps = {
    ref: contentRef,
    id: contentRef.current?.id,
    'aria-hidden': !isExpanded,
    style: {
      display: isExpanded ? 'block' : 'none'
    }
  }

  return {
    isExpanded,
    toggle,
    open,
    close,
    triggerProps,
    contentProps,
    triggerRef,
    contentRef
  }
}